/*
 * GetDiscountPreview
 *
 * Fetches the information about a discount, including the priceRules section.
 * This is for showing a "preview" of what the discount amount will be (before
 * we know what SKU it will be applied to)
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { gql, type Variables } from 'graphql-request';

import useCurrency from '@/app/hooks/useCurrency';
import gqlRequest from '@/utils/gqlRequest';

import type {
  GetDiscountPreview,
  GetDiscountPreview_content_discount,
  GetDiscountPreviewVariables as ReqVariables,
} from './types/GetDiscountPreview';

const query = gql`
  query GetDiscountPreview($discountCode: String!, $currencyCode: String) {
    content(currency: $currencyCode) {
      discount(discountCode: $discountCode) {
        priceRules {
          amount {
            amount
            currencyCode
          }
        }
      }
    }
  }
`;

export function withQueryKey(req: ReqVariables) {
  return {
    queryKey: ['GetDiscountPreview', req.discountCode, req.currencyCode],
    queryFn: async () => {
      if (!req.discountCode) return null;

      const data = await gqlRequest<GetDiscountPreview>(
        query,
        req as unknown as Variables,
      );

      return data.content?.discount;
    },
  };
}

export function useDiscountPreview(
  discountCode = '',
): UseQueryResult<
  GetDiscountPreview_content_discount | null | undefined,
  Error
> {
  const { currencyCode } = useCurrency();
  const variables: ReqVariables = {
    discountCode,
    currencyCode,
  };

  return useQuery({
    ...withQueryKey({
      ...variables,
    }),
  });
}
